/**
 * Auth provider — wraps the app, hydrates the user from a stored JWT
 * on mount, and exposes login / logout / refresh handlers.
 *
 * Composition note:
 *   - This file exports ONLY the `AuthProvider` component, satisfying
 *     react-refresh's "only-export-components" rule so fast refresh
 *     keeps working in every file that imports auth state.
 *   - The context object and its value type live in
 *     `@/context/authContextValue` (so `useAuth` can read them
 *     without re-mounting the provider on every edit).
 *   - The consumer hook lives in `@/context/useAuth`.
 */
import { useApolloClient } from '@apollo/client';
import {
  type AppAbility,
  createEmptyAbility,
  defineAbilityFor,
  isRootAdmin as computeIsRootAdmin,
} from '@redirector/front/auth/ability';
import { AuthContext } from '@redirector/front/context/authContextValue';
import { GET_USER_PROFILE } from '@redirector/front/gql/operations';
import type { User } from '@redirector/front/types/types';
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';

const TOKEN_KEY = 'redirector_token';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  // Decide the starting `loading` value synchronously: if there is no
  // stored token (or it is already expired), we know up-front that
  // there's nothing to validate and `loading` should start at `false`.
  // This lets us avoid a synchronous setState inside the mount effect
  // (the React 19 lint rule `set-state-in-effect` flags it as the
  // wrong place for that work) without introducing a flicker on
  // first paint for unauthenticated visitors.
  const [token, setToken] = useState<string | null>(() => {
    const stored = localStorage.getItem(TOKEN_KEY);
    if (!stored) return null;
    try {
      const payload = JSON.parse(atob(stored.split('.')[1]));
      if (payload.exp && payload.exp * 1000 < Date.now()) {
        localStorage.removeItem(TOKEN_KEY);
        return null;
      }
      return stored;
    } catch {
      localStorage.removeItem(TOKEN_KEY);
      return null;
    }
  });
  const [loading, setLoading] = useState(() => {
    // Mirror the token initialiser above: if we have no token to
    // validate, we are not loading.
    return localStorage.getItem(TOKEN_KEY) !== null;
  });
  const client = useApolloClient();
  const navigate = useNavigate();

  // Memoise ability + rootAdmin flag so downstream memos don't bust on
  // reference change.
  const ability = useMemo<AppAbility>(
    () => (user ? defineAbilityFor(user) : createEmptyAbility()),
    [user],
  );
  const isRootAdmin = useMemo(() => computeIsRootAdmin(user), [user]);

  const refreshUser = useCallback(async () => {
    const { data } = await client.query({
      query: GET_USER_PROFILE,
      fetchPolicy: 'network-only',
    });
    // getUserProfile is non-null in the schema; failures reject and are
    // handled by the caller.
    setUser(data.getUserProfile as User);
  }, [client]);

  // On mount: if a valid token survived the lazy initialiser above,
  // fetch the user profile to hydrate `user`. If the request fails we
  // clear the token (it was probably revoked or signed by a previous
  // server). The "no stored token" path was already handled at init
  // time, so we don't need to setState synchronously inside the
  // effect — that's what the eslint rule is asking us to avoid.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    refreshUser()
      .catch(() => {
        if (cancelled) return;
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // We deliberately depend on `token` only for the initial mount
    // hydration. `login()` and `logout()` mutate `token` directly and
    // also set `user`, so re-running this effect for them would either
    // be a no-op or cause a double fetch — exactly what we don't want.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = useCallback(
    (newToken: string, newUser: User) => {
      localStorage.setItem(TOKEN_KEY, newToken);
      setToken(newToken);
      setUser(newUser);
      navigate('/');
    },
    [navigate],
  );

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setUser(null);
    client.resetStore();
    navigate('/login');
  }, [client, navigate]);

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        ability,
        isRootAdmin,
        isAuthenticated: !!token && !!user,
        loading,
        login,
        logout,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
